import { getDurableItem, removeDurableItem, setDurableItem } from '../../../../vrs-auth/storage';
import { mobileLog } from '../../logging';
/**
 * Bound the UI wait without letting later cleanup overtake a native write.
 *
 * @param {Promise<T>} operation - Actual storage operation, retained after timeout.
 * @param {T} fallback - Unconfirmed outcome returned to the caller.
 * @returns {Promise<T>} Bounded caller result.
 */
function bounded(operation, fallback) {
    return new Promise(resolve => {
        const timer = setTimeout(() => resolve(fallback), 15000);
        operation.then(value => {
            clearTimeout(timer);
            resolve(value);
        }, () => {
            clearTimeout(timer);
            resolve(fallback);
        });
    });
}
/**
 * Ordered durable snapshots shared by native VRI read-only caches.
 *
 * @param {Array<string>} keys - Existing storage keys owned by this cache group.
 * @param {string} logPrefix - Static metadata-only diagnostic prefix.
 * @returns {Object} Isolated read, hydrate, save and cleanup operations.
 */
export function createVriReadCache(keys, logPrefix) {
    const snapshots = new Map();
    const revisions = new Map();
    let epoch = 0;
    let writes = Promise.resolve(true);
    let cleanupFailed = false;
    let cleanupOperation = null;
    /**
     * Only a matching owner can read the current process's checked snapshot.
     *
     * @param {CacheKey} key - Supported cache key.
     * @param {string} owner - Current checked account owner.
     * @returns {unknown} Snapshot for caller schema validation, or null.
     */
    function read(key, owner) {
        const snapshot = snapshots.get(key);
        return !cleanupFailed && owner && snapshot?.owner === owner ? snapshot.value : null;
    }
    /**
     * Read disk independently of the best-effort auth/preference mirrors.
     *
     * @param {CacheKey} key - Supported cache key.
     * @param {string} owner - Current checked account owner.
     * @param {Function} current - Account and view lifetime check.
     * @returns {Promise<CacheRead>} Checked read outcome, distinct from missing data.
     */
    async function hydrate(key, owner, current) {
        if (!current() || (cleanupFailed && !await bounded(cleanupOperation || clear(), false))) {
            return { ok: false,
                value: null };
        }
        const started = epoch;
        const revision = revisions.get(key);
        let accepting = true;
        const readDisk = async () => {
            await writes;
            if (!accepting || cleanupFailed || started !== epoch || !current()) {
                return { ok: false,
                    value: null };
            }
            const raw = await getDurableItem(key);
            const value = raw === null ? null : JSON.parse(raw);
            if (!accepting || started !== epoch || !current()) {
                return { ok: false,
                    value: null };
            }
            if (revision === revisions.get(key) && snapshots.get(key)?.owner !== owner) {
                snapshots.set(key, { owner,
                    persisted: true,
                    value });
            }
            return { ok: snapshots.get(key)?.persisted === true,
                value: read(key, owner) };
        };
        const result = await bounded(readDisk(), { ok: false,
            value: null });
        accepting = false;
        if (!result.ok && started === epoch && current()) {
            mobileLog('warn', `${logPrefix}_read_failed`, {});
        }
        return result;
    }
    /**
     * Keep the confirmed server result in memory; report disk persistence separately.
     *
     * @param {CacheKey} key - Supported cache key.
     * @param {string} owner - Current checked account owner.
     * @param {unknown} value - Schema-checked record supplied by the feature.
     * @param {Function} current - Account lifetime check.
     * @returns {Promise<boolean>} Whether persistence and its readback were confirmed.
     */
    function save(key, owner, value, current) {
        if (!current()) {
            return Promise.resolve(false);
        }
        const started = epoch;
        const raw = JSON.stringify(value);
        revisions.set(key, (revisions.get(key) || 0) + 1);
        const snapshot = { owner,
            persisted: false,
            value };
        snapshots.set(key, snapshot);
        writes = writes.then(async () => {
            if (started !== epoch || !current() || cleanupFailed) {
                return false;
            }
            await setDurableItem(key, raw);
            const saved = await getDurableItem(key);
            const confirmed = saved === raw && started === epoch && current();
            if (confirmed && snapshots.get(key) === snapshot) {
                snapshot.persisted = true;
            }
            return confirmed;
        }).catch(() => false);
        return bounded(writes, false).then(ok => {
            if (!ok && started === epoch && current()) {
                mobileLog('warn', `${logPrefix}_write_failed`, {});
            }
            return ok;
        });
    }
    /**
     * Fence old writes immediately, then remove disk snapshots after started writes settle.
     *
     * @returns {Promise<boolean>} Whether every disk removal was confirmed.
     */
    function clear() {
        const started = ++epoch;
        snapshots.clear();
        cleanupFailed = true;
        writes = writes.then(async () => {
            let removed = true;
            for (const key of keys) {
                try {
                    await removeDurableItem(key);
                    if (await getDurableItem(key) !== null) {
                        removed = false;
                    }
                }
                catch {
                    removed = false;
                }
            }
            if (removed && started === epoch) {
                cleanupFailed = false;
            }
            return removed;
        }).catch(() => false);
        const operation = writes;
        cleanupOperation = operation;
        operation.then(() => {
            if (cleanupOperation === operation) {
                cleanupOperation = null;
            }
        });
        return bounded(operation, false);
    }
    return { clear,
        hydrate,
        read,
        save };
}
